import { IApprovalRequest } from '../../definitions/Approval'
import { IPagination, IPaginationQueryResponse } from '../../definitions/Common'
import { IContract } from '../../definitions/Contract'
import { IContractSchema } from '../../definitions/ContractSchema'
import { ICMAutocomplete } from '../../definitions/MiscSchema'
import { CopyFromFlowItem, FlowDeletionResponse, IAncestorFlow, IFlow, IFlowResponse, IFlowWithApproval, ISimpleFlow, ISubFlowIO, NodeType } from '../../definitions/Nodes'
import { IExecEnvComparison, IExecutionEnvironment, IExecutionEnvironmentArtifact, IFlowRunConfig, IMultipleFlowsConfigs, INodeExecEnvDataResponse, IPollingMultipleFlows, ISimpleExecutionEnvironment } from '../../definitions/NodesExecution'
import { ISettlementResultResponse, ISettlementResultSchema, ISettlementSchema } from '../../definitions/SettlementSchema'
import { IUser } from '../../definitions/Users'
import { IVersioning } from '../../definitions/Versioning'
import ApiUrlCompiler from '../api_urls/ApiUrlCompiler'
import API_URLS from '../ApiUrls'
import HttpClient from '../HttpClient'

class FlowApiClientClass {
    createFlow = async (contractId: number, globals: IFlow['globals'], versioning?: IVersioning, fromFlowId?: string): Promise<IFlow> => {
        const { url, headers } = ApiUrlCompiler.compile(API_URLS.settlement.createFlow)
        const body = { contractId, globals, versioning, fromFlowId }

        const res = await HttpClient.post<IFlow>(url, body, headers)
        return res
    }

    getFlows = async (pagination: IPagination): Promise<IPaginationQueryResponse<IFlowResponse>> => {
        const { url, headers } = ApiUrlCompiler.compile(API_URLS.settlement.getFlows)
        const body = { pagination }

        const res = await HttpClient.post<IPaginationQueryResponse<IFlowResponse>>(url, body, headers)
        return res
    }

    getSettlementResults = async (pagination: IPagination): Promise<IPaginationQueryResponse<ISettlementResultSchema>> => {
        const { url, headers } = ApiUrlCompiler.compile(API_URLS.settlement.getSettlementResults)
        const body = { pagination }

        const res = await HttpClient.post<IPaginationQueryResponse<ISettlementResultSchema>>(url, body, headers)
        return res
    }

    getSubFlows = async (): Promise<ISimpleFlow[]> => {
        const { url, headers } = ApiUrlCompiler.compile(API_URLS.settlement.getSubFlows)
        const body = {}

        const res = await HttpClient.post<ISimpleFlow[]>(url, body, headers)
        return res
    }

    getSubFlowIO = async (subFlowId: string, flowId: string): Promise<ISubFlowIO> => {
        const { url, headers } = ApiUrlCompiler.compile(API_URLS.settlement.getSubFlowIO)
        const body = { subFlowId, flowId }

        const res = await HttpClient.post<ISubFlowIO>(url, body, headers)
        return res
    }

    getFlow = async (flowId: string, flowRunConfig?: IFlowRunConfig): Promise<IFlow> => {
        const { url, headers } = ApiUrlCompiler.compile(API_URLS.settlement.getFlow)
        const body = { flowId, flowRunConfig }

        const res = await HttpClient.post<IFlow>(url, body, headers)
        return res
    }

    updateFlow = async (flow: IFlow): Promise<IFlow> => {
        const { url, headers } = ApiUrlCompiler.compile(API_URLS.settlement.updateFlow)
        const body = { flow }

        const res = await HttpClient.post<IFlow>(url, body, headers)
        return res
    }

    startFlow = async (flowRunConfig: IFlowRunConfig): Promise<IExecutionEnvironment> => {
        const { url, headers } = ApiUrlCompiler.compile(API_URLS.settlement.startFlow)
        const body = { flowRunConfig }

        const res = await HttpClient.post<IExecutionEnvironment>(url, body, headers)
        return res
    }

    startDryRun = async (flowRunConfig: IFlowRunConfig): Promise<IExecutionEnvironment> => {
        const { url, headers } = ApiUrlCompiler.compile(API_URLS.settlement.startDryRun)
        const body = { flowRunConfig }

        const res = await HttpClient.post<IExecutionEnvironment>(url, body, headers)
        return res
    }

    executeFlow = async (execEnvId: string, batch?: boolean): Promise<IExecutionEnvironment> => {
        const { url, headers } = ApiUrlCompiler.compile(API_URLS.settlement.executeFlow)
        const body = { executionEnvironmentId: execEnvId, batch }

        const res = await HttpClient.post<IExecutionEnvironment>(url, body, headers)
        return res
    }

    startMultipleFlows = async (contractUUIDs: string[], runConfigs: IMultipleFlowsConfigs, runMode: 'full' | 'dry'): Promise<string[]> => {
        const { url, headers } = ApiUrlCompiler.compile(API_URLS.settlement.startMultipleFlows)
        const body = { contractUUIDs, runConfigs, runMode }

        const res = await HttpClient.post<string[]>(url, body, headers)
        return res
    }

    deleteFlow = async (flowId: string): Promise<FlowDeletionResponse> => {
        const { url, headers } = ApiUrlCompiler.compile(API_URLS.settlement.deleteFlow)
        const body = { flowId }

        const res = await HttpClient.post<FlowDeletionResponse>(url, body, headers)
        return res
    }

    getFlowExecutionEnvironment = async (eeId: string): Promise<IExecutionEnvironment> => {
        const { url, headers } = ApiUrlCompiler.compile(API_URLS.settlement.getFlowExecutionEnvironment)
        const body = { eeId }

        const res = await HttpClient.post<IExecutionEnvironment>(url, body, headers)
        return res
    }

    getAllVersionFlows = async (ancestorKey: string): Promise<IFlowWithApproval[]> => {
        const { url, headers } = ApiUrlCompiler.compile(API_URLS.settlement.getAllVersionFlows)
        const body = { ancestorKey }

        const res = await HttpClient.post<IFlowWithApproval[]>(url, body, headers)
        return res
    }

    createNewFlowVersion = async (flowId: string): Promise<ISimpleFlow> => {
        const { url, headers } = ApiUrlCompiler.compile(API_URLS.settlement.createNewFlowVersion)
        const body = { flowId }

        const res = await HttpClient.post<ISimpleFlow>(url, body, headers)
        return res
    }

    getNodeAuditData = async (nodeId: string, eeId: string, memoryId?: string, page?: number): Promise<INodeExecEnvDataResponse> => {
        const { url, headers } = ApiUrlCompiler.compile(API_URLS.settlement.getNodeAuditData)
        const body = { nodeId, eeId, memoryId, page }

        const res = await HttpClient.post<INodeExecEnvDataResponse>(url, body, headers)
        return res
    }

    getNodeCompareData = async (nodeId: string, execEnvId: string, comparedExecEnvId: string): Promise<INodeExecEnvDataResponse> => {
        const { url, headers } = ApiUrlCompiler.compile(API_URLS.settlement.getNodeCompareData)
        const body = { nodeId, execEnvId, comparedExecEnvId }

        const res = await HttpClient.post<INodeExecEnvDataResponse>(url, body, headers)
        return res
    }

    getSettlement = async (eeId: string): Promise<ISettlementSchema> => {
        const { url, headers } = ApiUrlCompiler.compile(API_URLS.settlement.getSettlement)
        const body = { eeId }

        const res = await HttpClient.post<ISettlementSchema>(url, body, headers)
        return res
    }

    getAllArtifacts = async (eeId: string): Promise<IExecutionEnvironmentArtifact[]> => {
        const { url, headers } = ApiUrlCompiler.compile(API_URLS.settlement.getAllArtifacts)
        const body = { eeId }

        const res = await HttpClient.post<IExecutionEnvironmentArtifact[]>(url, body, headers)
        return res
    }

    downloadAllArtifacts = async (eeId: string): Promise<string[]> => {
        const { url, headers } = ApiUrlCompiler.compile(API_URLS.settlement.downloadAllArtifacts)
        const body = { eeId }

        const res = await HttpClient.post<string[]>(url, body, headers)
        return res
    }

    downloadArtifact = async (eeId: string, artifactId: string): Promise<string> => {
        const { url, headers } = ApiUrlCompiler.compile(API_URLS.settlement.downloadArtifact)
        const body = { eeId, artifactId }

        const res = await HttpClient.post<string>(url, body, headers)
        return res
    }

    createSubFlow = async (flowId: string, selectedNodesId?: string[], exeSubFlowNodeId?: string): Promise<IFlow> => {
        const { url, headers } = ApiUrlCompiler.compile(API_URLS.settlement.createSubFlow)
        const body = { flowId, selectedNodesId, exeSubFlowNodeId }

        const res = await HttpClient.post<IFlow>(url, body, headers)
        return res
    }

    updateFavoriteNodeList = async (favoriteNode: NodeType[], userUuid: string): Promise<NodeType[]> => {
        const { url, headers } = ApiUrlCompiler.compile(API_URLS.settlement.updateFavoriteNodeList)
        const body = { favoriteNode, userUuid }

        const res = await HttpClient.post<NodeType[]>(url, body, headers)
        return res
    }

    getFlowExecutionEnvironments = async (flowId: string, pagination?: IPagination): Promise<IPaginationQueryResponse<ISimpleExecutionEnvironment>> => {
        const { url, headers } = ApiUrlCompiler.compile(API_URLS.settlement.getFlowExecutionEnvironments)
        const body = { flowId, pagination }

        const res = await HttpClient.post<IPaginationQueryResponse<ISimpleExecutionEnvironment>>(url, body, headers)
        return res
    }

    getTotalExecEnvComparison = async (currentExecEnvId: string, comparedExecEnvId: string): Promise<IExecEnvComparison> => {
        const { url, headers } = ApiUrlCompiler.compile(API_URLS.settlement.getTotalExecEnvComparison)
        const body = { currentExecEnvId, comparedExecEnvId }

        const res = await HttpClient.post<IExecEnvComparison>(url, body, headers)
        return res
    }

    getHeadFlow = async (ancestorKey: string): Promise<ISimpleFlow> => {
        const { url, headers } = ApiUrlCompiler.compile(API_URLS.settlement.getHeadFlow)
        const body = { ancestorKey }

        const res = await HttpClient.post<ISimpleFlow>(url, body, headers)
        return res
    }

    compileFlow = async (flowId: string): Promise<IExecutionEnvironment> => {
        const { url, headers } = ApiUrlCompiler.compile(API_URLS.settlement.compileFlow)
        const body = { flowId }

        const res = await HttpClient.post<IExecutionEnvironment>(url, body, headers)
        return res
    }

    getDbMetadata = async (): Promise<ICMAutocomplete[]> => {
        const { url, headers } = ApiUrlCompiler.compile(API_URLS.settlement.getDbMetadata)
        const body = {}

        const res = await HttpClient.post<ICMAutocomplete[]>(url, body, headers)
        return res
    }

    getListCopyFromFlow = async (): Promise<CopyFromFlowItem[]> => {
        const { url, headers } = ApiUrlCompiler.compile(API_URLS.settlement.getListCopyFromFlow)
        const body = {}

        const res = await HttpClient.post<CopyFromFlowItem[]>(url, body, headers)
        return res
    }

    replaceFlow = async (flowUUID: string, options: { nodes: IFlow['nodes'], globals: IFlow['globals'] }): Promise<IFlow> => {
        const { url, headers } = ApiUrlCompiler.compile(API_URLS.settlement.replaceFlow)
        const body = { flowUUID, options }

        const res = await HttpClient.post<IFlow>(url, body, headers)
        return res
    }

    pollingMultipleFlows = async (executionEnvironmentUUIDs: string[]): Promise<IPollingMultipleFlows[]> => {
        const { url, headers } = ApiUrlCompiler.compile(API_URLS.settlement.pollingMultipleFlows)
        const body = { executionEnvironmentUUIDs }

        const res = await HttpClient.post<IPollingMultipleFlows[]>(url, body, headers)
        return res
    }

    getSettlementResult = async (executionEnvironmentUUIDs: string[]): Promise<ISettlementResultResponse> => {
        const { url, headers } = ApiUrlCompiler.compile(API_URLS.settlement.getSettlementResult)
        const body = { executionEnvironmentUUIDs }

        const res = await HttpClient.post<ISettlementResultResponse>(url, body, headers)
        return res
    }

    createSettlementResult = async (contractUUIDs: string[], executionEnvironmentUUIDs: string[], runConfig: IMultipleFlowsConfigs): Promise<ISettlementResultSchema> => {
        const { url, headers } = ApiUrlCompiler.compile(API_URLS.settlement.createSettlementResult)
        const body = { contractUUIDs, executionEnvironmentUUIDs, runConfig }

        const res = await HttpClient.post<ISettlementResultSchema>(url, body, headers)
        return res
    }

    getApprovedFlowTemplates = async (): Promise<(IFlow & { approval?: Pick<IApprovalRequest, 'approvalEntityUUID' | 'status'>, user?: Pick<IUser, 'name' | 'fullName' | 'image'> })[]> => {
        const { url, headers } = ApiUrlCompiler.compile(API_URLS.settlement.getApprovedFlowTemplates)
        const body = {}

        const res = await HttpClient.post<(IFlow & { approval?: Pick<IApprovalRequest, 'approvalEntityUUID' | 'status'> })[]>(url, body, headers)
        return res
    }

    getApprovedFlowTemplateNames = async (): Promise<IAncestorFlow[]> => {
        const { url, headers } = ApiUrlCompiler.compile(API_URLS.settlement.getApprovedFlowTemplateNames)
        const body = {}

        const res = await HttpClient.post<IAncestorFlow[]>(url, body, headers)
        return res
    }

    getPendingFlowTemplates = async (): Promise<(IFlow & { approval?: Pick<IApprovalRequest, 'approvalEntityUUID' | 'status'>, user?: Pick<IUser, 'name' | 'fullName' | 'image'> })[]> => {
        const { url, headers } = ApiUrlCompiler.compile(API_URLS.settlement.getPendingFlowTemplates)
        const body = {}

        const res = await HttpClient.post<(IFlow & { approval?: Pick<IApprovalRequest, 'approvalEntityUUID' | 'status'> })[]>(url, body, headers)
        return res
    }

    createFlowTemplate = async (name: string, fromFlowTemplateUUID?: string): Promise<IFlow> => {
        const { url, headers } = ApiUrlCompiler.compile(API_URLS.settlement.createFlowTemplate)
        const body = { name, fromFlowTemplateUUID }

        const res = await HttpClient.post<IFlow>(url, body, headers)
        return res
    }

    getListCopyFromFlowTemplate = async (): Promise<IFlow[]> => {
        const { url, headers } = ApiUrlCompiler.compile(API_URLS.settlement.getListCopyFromFlowTemplate)
        const body = {}

        const res = await HttpClient.post<IFlow[]>(url, body, headers)
        return res
    }

    updateSettlementEngineRef = async (contractUUID: string, settlementRef: IContract['settlementRef']): Promise<IContract> => {
        const { url, headers } = ApiUrlCompiler.compile(API_URLS.settlement.updateSettlementEngineRef)
        const body = { contractUUID, settlementRef }

        const res = await HttpClient.post<IContract>(url, body, headers)
        return res
    }

    getSettlementEngineRefInfo = async (settlementEngineUUID: string, ancestorKey: string): Promise<{ name: string, version: number, status: IApprovalRequest['status'] }> => {
        const { url, headers } = ApiUrlCompiler.compile(API_URLS.settlement.getSettlementEngineRefInfo)
        const body = { settlementEngineUUID, ancestorKey }

        const res = await HttpClient.post<{ name: string, version: number, status: IApprovalRequest['status'] }>(url, body, headers)
        return res
    }

    getAncestorEngines = async (ancestorUUID: string): Promise<IAncestorFlow> => {
        const { url, headers } = ApiUrlCompiler.compile(API_URLS.settlement.getAncestorEngines)
        const body = { ancestorUUID }

        const res = await HttpClient.post<IAncestorFlow>(url, body, headers)
        return res
    }

    getSettlementsByContractId = async (contractId: number): Promise<Pick<ISettlementSchema, 'contractId' | 'uuid' | 'periodStart' | 'periodEnd' | 'runAt' | 'type'>[]> => {
        const { url, headers } = ApiUrlCompiler.compile(API_URLS.settlement.getSettlementsByContractId)
        const body = { contractId }

        const res = await HttpClient.post<Pick<ISettlementSchema, 'contractId' | 'uuid' | 'periodStart' | 'periodEnd' | 'runAt' | 'type'>[]>(url, body, headers)
        return res
    }

    getSettlementSummaryByUUID = async (settlementUUID: string): Promise<Pick<ISettlementSchema, 'uuid' | 'summary'>> => {
        const { url, headers } = ApiUrlCompiler.compile(API_URLS.settlement.getSettlementSummaryByUUID)
        const body = { settlementUUID }

        const res = await HttpClient.post<Pick<ISettlementSchema, 'uuid' | 'summary'>>(url, body, headers)
        return res
    }

    getSettlementByExecutionEnvironmentUUID = async (executionEnvironmentUUID: string): Promise<Pick<ISettlementSchema, 'uuid' | 'contractId' | 'executionEnvironmentId'>> => {
        const { url, headers } = ApiUrlCompiler.compile(API_URLS.settlement.getSettlementByExecutionEnvironmentUUID)
        const body = { executionEnvironmentUUID }

        const res = await HttpClient.post<Pick<ISettlementSchema, 'uuid' | 'contractId' | 'executionEnvironmentId'>>(url, body, headers)
        return res
    }

    getAIResponse = async (conversation: Array<{ role: 'user' | 'assistant', content: Array<{ text: string }> }>, currentSettlementUUID: string, comparingSettlementUUID: string): Promise<{ role: 'user' | 'assistant', content: Array<{ text: string }> }> => {
        const { url, headers } = ApiUrlCompiler.compile(API_URLS.settlement.getAIResponse)
        const body = { conversation, currentSettlementUUID, comparingSettlementUUID }

        const res = await HttpClient.post<{ role: 'user' | 'assistant', content: Array<{ text: string }> }>(url, body, headers)
        return res
    }

    getAllSchemaForContractNode = async (): Promise<Pick<IContractSchema, 'uuid' | 'name' | 'steps'>[]> => {
        const { url, headers } = ApiUrlCompiler.compile(API_URLS.settlement.getAllSchemaForContractNode)
        const body = {}

        const res = await HttpClient.post<Pick<IContractSchema, 'uuid' | 'name' | 'steps'>[]>(url, body, headers)
        return res
    }
}

const FlowApiClient = new FlowApiClientClass()
export default FlowApiClient
